import { ApiService } from '../apiService'
import type {
  ConversationDto,
  ChatMessageDto,
  MessagesResponse,
  SendMessageRequest,
  CreateDirectConversationRequest,
  CreateGroupConversationRequest,
  ChatUserSearchResult,
  ChatNotification,
  ChatImageUploadResponse,
} from '../types/chat'

export const chatApi = {
  // Get all conversations for the current user
  getConversations: () =>
    ApiService.get<ConversationDto[]>('/api/Chat/conversations'),

  // Get a specific conversation by ID
  getConversation: (conversationId: string) =>
    ApiService.get<ConversationDto>(`/api/Chat/conversations/${conversationId}`),

  // Get paginated messages for a conversation
  getMessages: (conversationId: string, page: number = 1, pageSize: number = 50) =>
    ApiService.get<ChatMessageDto[]>(
      `/api/Chat/conversations/${conversationId}/messages?page=${page}&pageSize=${pageSize}`
    ),

  // Send a message to a conversation
  sendMessage: (conversationId: string, request: SendMessageRequest) =>
    ApiService.post<ChatMessageDto>(
      `/api/Chat/conversations/${conversationId}/messages`,
      request
    ),

  // Create a direct conversation with another user
  createDirectConversation: (request: CreateDirectConversationRequest) =>
    ApiService.post<ConversationDto>('/api/Chat/conversations/direct', request),

  // Create a group conversation
  createGroupConversation: (request: CreateGroupConversationRequest) =>
    ApiService.post<ConversationDto>('/api/Chat/conversations/group', request),

  // Mark a conversation as read (GET messages marks it as read)
  markAsRead: (conversationId: string) =>
    ApiService.get<void>(`/api/Chat/conversations/${conversationId}/messages`),

  // Search for users to start a chat with
  searchUsers: (query: string) =>
    ApiService.get<ChatUserSearchResult[]>(`/api/Chat/users/search?q=${encodeURIComponent(query)}`),

  // Get online friends

  // Get chat notifications
  getNotifications: () =>
    ApiService.get<ChatNotification[]>('/api/Chat/notifications'),

  // Get unread message count
  getUnreadCount: () =>
    ApiService.get<number>('/api/ChatNotification/unread-count'),

  // Mark all notifications as read
  markAllNotificationsRead: () =>
    ApiService.post<void>('/api/ChatNotification/mark-all-read', {}),

  // Delete a message
  deleteMessage: (conversationId: string, messageId: string) =>
    ApiService.delete<void>(`/api/Chat/conversations/${conversationId}/messages/${messageId}`),

  // Edit a message
  editMessage: (conversationId: string, messageId: string, content: string) =>
    ApiService.put<ChatMessageDto>(
      `/api/Chat/conversations/${conversationId}/messages/${messageId}`,
      { content }
    ),

  // Leave a conversation (group or adventure)
  leaveConversation: (conversationId: string) =>
    ApiService.post<void>(`/api/Chat/conversations/${conversationId}/leave`, {}),

  // Mute a conversation
  muteConversation: (conversationId: string, muted: boolean) =>
    ApiService.post<void>(`/api/Chat/conversations/${conversationId}/mute`, { muted }),

  // Pin a conversation
  pinConversation: (conversationId: string, pinned: boolean) =>
    ApiService.post<void>(`/api/Chat/conversations/${conversationId}/pin`, { pinned }),

  // Add participants to a group conversation
  addParticipants: (conversationId: string, participantIds: string[]) =>
    ApiService.post<void>(`/api/Chat/conversations/${conversationId}/participants`, { participantIds }),

  // Remove a participant from a group conversation
  removeParticipant: (conversationId: string, participantId: string) =>
    ApiService.delete<void>(`/api/Chat/conversations/${conversationId}/participants/${participantId}`),

  // Upload an image for chat messages
  uploadImage: async (file: File): Promise<ChatImageUploadResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<ChatImageUploadResponse>('/api/Chat/images', formData)
  },

  // Get or join a group chat conversation
  getOrJoinGroupChat: (groupId: string) =>
    ApiService.post<ConversationDto>(`/api/Chat/conversations/group/${groupId}/join`, {}),

  // Get or join an adventure chat conversation
  getOrJoinAdventureChat: (adventureId: string) =>
    ApiService.post<ConversationDto>(`/api/Chat/conversations/adventure/${adventureId}/join`, {}),
}
